import type { Dispatch, SetStateAction } from 'react';
import type { FeapiError } from '@edreamsodigeo/frontend-api-client';

import type {
  CheckoutRequest,
  CollectionMethod,
  IdealIssuerBankType,
  Maybe,
  PaymentMethod,
} from '@/generated/graphql';
import type { StoredCardRequest } from './StoredCardRequest';
import type { UseMutationType } from '@/mutations/types';
import type {
  CreditCardCollectionRequest,
  ResetCreditCardIndividualField,
} from '@/components/CreditCardForm/types';
import type { PitDetailsFromRetailSessionContextType } from './PitDetailsFromRetailSessionTypes';

export enum StoredCreditCardType {
  STORED_CARD = 'STORED_CARD',
  RETAIL_SESSION = 'RETAIL_SESSION',
}
export type SelectedPaymentMethodType = PaymentMethod | StoredCreditCardType;

export interface SelectedPaymentMethod {
  collectionMethodType?: SelectedPaymentMethodType;
  creditCardCollection?: Maybe<
    Partial<CreditCardCollectionRequest> & {
      first8Digits?: string; // Only for Payment Form AB, first 8 digits are used in different flows like Prime (including Prime EML) and Flash Offers
      paymentInstrumentToken?: string; // For Prime Decoupling project, when the selected method is from RETAIL_SESSION, this value will be set
    }
  >;
  idealIssuerBank?: IdealIssuerBankType | '';
  storedCard?: Maybe<Partial<StoredCardRequest>>;
  retailSession?: Maybe<Partial<PitDetailsFromRetailSessionContextType>>;
  valid: boolean;
  id?: string | number;
}
export interface StoredCardRequestType {
  storedCard?: Partial<StoredCardRequest> | null;
  isStoredCardSelected?: boolean;
  isValid?: boolean;
}

export interface CheckoutStateContextType {
  selectedPaymentMethod: SelectedPaymentMethod;
  storedCardRequest?: StoredCardRequestType;
  reset: boolean;
  resetIndividualFields: ResetCreditCardIndividualField;
  isValidCheckoutForm: boolean;
}
export type CheckoutResponseStateContextType = Omit<UseMutationType, 'mutate'>;
export interface CheckoutPrimeEMLStatusContextType {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export interface InvoiceStateContextType {
  details?: CheckoutRequest['invoice'];
  valid: boolean;
}
export interface SpecialRequestStateContextType {
  valid: boolean;
  value?: string;
}
export type ShowSumbmitValidationContextType = boolean;

export interface AcquisitionContextType {
  setAcquisition: Dispatch<SetStateAction<boolean>>;
  isAcquisition: boolean;
}

export interface CheckoutCollectionOptionsStateContextType {
  collectionOptions: Array<CollectionMethod>;
  loading?: boolean;
  errors?: Array<FeapiError>;
}
export type CheckoutFormRefStateContextType = (
  ref: React.RefObject<HTMLDivElement>,
  section?: string
) => void;

export type ShoppingBasketIdContextType = null | string;

export interface CreditCardPCIDataType {
  firstNumberDigits: string;
  expirationMonth: string;
  expirationYear: string;
}

export type PaymentInstrumentTokenContextType = string;

export type AlternativePaymentMethodSelected = boolean;
